// src/components/WebParts/HeroSection.tsx
import React from "react";
import { Linkedin, Instagram, Facebook, ChevronRight } from "lucide-react";
import CountUp from "react-countup";

const TITLE = "SPRINTCORE";

const HeroSection: React.FC = () => {
  return (
    <section
      className="min-h-screen flex items-center bg-black text-white
                 py-14 sm:py-16 md:py-20
                 px-5 sm:px-6 md:px-10 lg:px-20
                 relative overflow-hidden"
    >
      {/* Fondo blur con lazy loading */}
      <div className="absolute inset-0 z-0 lg:blur-md overflow-hidden">
        <img
          src={
            "https://res.cloudinary.com/xvzxvfee/image/upload/v1788743200/fondo_kk4tqb.webp"
          }
          alt=""
          loading="lazy"
          decoding="async"
          aria-hidden="true"
          className="h-full w-full object-cover object-center"
          style={{
            WebkitMaskImage:
              "radial-gradient(120% 120% at 50% 50%, #000 55%, transparent 95%)",
            maskImage:
              "radial-gradient(120% 120% at 50% 50%, #000 55%, transparent 95%)",
          }}
        />
      </div>

      {/* Imagen bg con lazy loading */}
      <div
        className="hidden lg:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-0
                   w-full h-full opacity-90 edge-fade-mask overflow-hidden"
      >
        <img
          src={
            "https://res.cloudinary.com/xvzxvfee/image/upload/v1788743200/fondo_kk4tqb.webp"
          }
          alt=""
          loading="lazy"
          decoding="async"
          aria-hidden="true"
          className="h-full w-full object-contain object-center"
        />
      </div>

      {/* CONTENIDO */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-14 lg:gap-16 w-full max-w-[90rem] mx-auto relative z-10">
        {/* Columna izquierda */}
        <div className="space-y-7 sm:space-y-8 text-center lg:text-left">
          {/* Título principal */}
          <h1
            className="
              font-extrabold text-white relative
              leading-[0.92]
              tracking-[0.06em] sm:tracking-[0.10em] lg:tracking-widest
              text-[clamp(2.4rem,9vw,4.8rem)]
              select-none
              mt-8 sm:mt-0
            "
          >
            <span className="relative inline-block title-track select-none">
              <span className="relative z-10 inline-flex">
                {[...TITLE].map((ch, i) => (
                  <span key={`fg-${i}`} className="relative inline-block">
                    <span className="relative">{ch}</span>
                    <span
                      aria-hidden
                      className="pointer-events-none absolute inset-0 glitch-frag"
                    >
                      {ch}
                    </span>
                  </span>
                ))}
              </span>

              <span
                aria-hidden
                className="title-glow absolute inset-0 pointer-events-none glow-breathe"
              >
                <span className="inline-flex">
                  {[...TITLE].map((ch, i) => (
                    <span
                      key={`glow-${i}`}
                      className="relative inline-block [text-shadow:0_0_6px_#fff,0_0_12px_#fff,0_0_18px_#ff4da6,0_0_26px_#D69900,0_0_36px_#FFED62]"
                    >
                      {ch}
                    </span>
                  ))}
                </span>
              </span>
            </span>
          </h1>

          {/* Subtítulo */}
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-widest leading-tight -translate-y-4">
            <span className="bg-gradient-to-r from-prin4 to-prin2 bg-clip-text text-transparent">
              INNO
            </span>
            <span className="text-transparent stroke-yellow">VANDO</span>{" "}
            <span className="bg-gradient-to-r from-prin2 to-prin3 bg-clip-text text-transparent">
              EL FUTURO
            </span>
          </h2>

          {/* Texto */}
          <p
            className="text-gray-300/95
                       max-w-none sm:max-w-xl md:max-w-2xl
                       text-sm sm:text-base md:text-lg
                       leading-relaxed whitespace-normal break-words
                       mx-auto lg:mx-0"
          >
            <strong>
              Creamos soluciones digitales pensadas para hacer avanzar tu
              negocio.{" "}
            </strong>
            <br />
            Desde una página web o aplicación móvil hasta una plataforma
            completa, convertimos tus ideas en productos digitales funcionales,
            atractivos y preparados para crecer contigo.
          </p>

          {/* Métricas */}
          <div className="flex gap-8 sm:gap-12 text-center justify-center lg:justify-start">
            <div>
              <CountUp
                end={9}
                prefix="+"
                duration={3}
                className="text-2xl sm:text-3xl md:text-4xl font-semibold"
              />
              <p className="text-xs sm:text-sm md:text-base text-gray-400">
                Proyectos demo
              </p>
            </div>
            <div>
              <CountUp
                end={20}
                prefix="+"
                duration={3}
                className="text-2xl sm:text-3xl md:text-4xl font-semibold"
              />
              <p className="text-xs sm:text-sm md:text-base text-gray-400">
                Clientes satisfechos
              </p>
            </div>
            <div>
              <CountUp
                end={22}
                duration={3}
                prefix="+"
                className="text-2xl sm:text-3xl md:text-4xl font-semibold"
              />
              <p className="text-xs sm:text-sm md:text-base text-gray-400">
                Reseñas positivas
              </p>
            </div>
          </div>

          {/* Botón + GlitchText */}
          <div className="pt-3 sm:pt-4 w-full max-w-md mx-auto lg:mx-0">
            <button
              type="button"
              onClick={() => {
                document.getElementById("about")?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }}
              className="relative overflow-hidden text-base sm:text-lg md:text-xl font-semibold px-6 sm:px-7 md:px-8 py-3 rounded-full shadow-lg flex items-center justify-between gap-2 transition-all duration-300 w-full sm:max-w-[20.5rem]"
            >
              <span className="absolute inset-0 shine-fill pointer-events-none rounded-full" />
              <span className="absolute inset-0 shine-border pointer-events-none rounded-full" />

              <span className="relative z-10 text-white">Explorar más</span>

              <ChevronRight className="relative z-10 h-5 w-5 text-white" />
            </button>

            <p className="mt-5 sm:mt-16 text-white text-lg sm:text-xl md:text-2xl font-medium leading-snug text-center lg:text-left">
              ¿Tienes algo en mente?
              <br />
              Cuéntanos tu idea, necesidad o desafío. Nosotros te ayudamos a
              convertirlo en una solución digital.
            </p>
          </div>
        </div>

        {/* Columna derecha */}
        <div className="flex flex-col justify-between space-y-8">
          <div className="space-y-5 sm:space-y-6">
            {/* LinkedIn */}
            <div className="flex items-center justify-center lg:justify-end px-4 sm:px-6 py-2">
              <div className="mr-6 sm:mr-8 flex flex-col">
                <CountUp
                  end={8}
                  duration={6}
                  className="text-2xl sm:text-3xl md:text-4xl font-semibold"
                />
                <span className="text-xs sm:text-sm text-gray-300">
                  Seguidores
                </span>
              </div>

              <a
                href="https://www.linkedin.com/company/sprintcore"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Ir a LinkedIn"
                className="flex flex-col justify-evenly items-center aspect-square
                           w-24 sm:w-28 md:w-32
                           bg-black/50 hover:bg-yellow-600/50 transition-colors
                           rounded-xl border border-gray-700 hover:border-gray-400 cursor-pointer"
              >
                <Linkedin className="text-white" />
                <span className="text-xs sm:text-sm text-gray-200">
                  LinkedIn
                </span>
              </a>
            </div>

            {/* Instagram */}
            <div className="flex items-center justify-center lg:justify-end px-4 sm:px-6 py-2">
              <div className="mr-6 sm:mr-8 flex flex-col">
                <CountUp
                  end={18}
                  duration={8}
                  className="text-2xl sm:text-3xl md:text-4xl font-semibold"
                />
                <span className="text-xs sm:text-sm text-gray-300">
                  Seguidores
                </span>
              </div>

              <a
                href="https://www.instagram.com/sprintcore/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Ir a Instagram"
                className="flex flex-col justify-evenly items-center aspect-square
                           w-24 sm:w-28 md:w-32
                           bg-black/50 hover:bg-yellow-600/50 transition-colors
                           rounded-xl border border-gray-700 hover:border-gray-400 cursor-pointer"
              >
                <Instagram className="text-white" />
                <span className="text-xs sm:text-sm text-gray-200">
                  Instagram
                </span>
              </a>
            </div>

            {/* Facebook */}
            <div className="flex items-center justify-center lg:justify-end px-4 sm:px-6 py-2">
              <div className="mr-6 sm:mr-8 flex flex-col">
                <CountUp
                  end={101}
                  duration={12}
                  className="text-2xl sm:text-3xl md:text-4xl font-semibold"
                />
                <span className="text-xs sm:text-sm text-gray-300">
                  Seguidores
                </span>
              </div>

              <a
                href="https://www.facebook.com/profile.php?id=100095027568458"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Ir a Facebook"
                className="flex flex-col justify-evenly items-center aspect-square
                           w-24 sm:w-28 md:w-32
                           bg-black/45 hover:bg-yellow-600/50 transition-colors
                           rounded-xl border border-gray-700 hover:border-gray-400 cursor-pointer"
              >
                <Facebook className="text-white" />
                <span className="text-xs sm:text-sm text-gray-200">
                  Facebook
                </span>
              </a>
            </div>
          </div>

          <div className="px-4 sm:px-6 py-2 flex justify-center lg:justify-end">
            <p
              className="
                whitespace-normal break-words leading-relaxed
                w-full max-w-none sm:max-w-md md:max-w-lg
                text-sm sm:text-base md:text-lg
                text-center lg:text-right
              "
            >
              <strong>Diseñamos tecnología con propósito.</strong>
              <br />
              Cada proyecto combina estrategia, diseño y desarrollo para
              convertir una necesidad en una solución que aporte valor.
            </p>
          </div>
        </div>
      </div>

      {/* estilos internos */}
      <style>{`
        @keyframes glow-breathe {
          0%,100% { opacity: .28; filter: blur(0px); }
          50%     { opacity: .45; filter: blur(.5px); }
        }
        .glow-breathe { animation: glow-breathe 3.2s ease-in-out infinite; }

        @keyframes glitch-flicker {
          0%, 96%, 100% { opacity: 0; transform: translate(0,0); clip-path: inset(0 0 0 0); filter: hue-rotate(0deg) saturate(1); }
          97% { opacity: .9; transform: translate(0.5px,-0.5px); clip-path: inset(10% 0 12% 0); filter: hue-rotate(10deg) saturate(1.2); }
          98% { opacity: .75; transform: translate(-0.5px,0.6px); clip-path: inset(60% 0 5% 0); filter: hue-rotate(-8deg) saturate(1.15); }
          99% { opacity: .6; transform: translate(0.2px,-0.2px); clip-path: inset(30% 0 35% 0); filter: hue-rotate(5deg) saturate(1.1); }
        }
        .glitch-frag {
          mix-blend-mode: screen;
          animation: glitch-flicker 8s steps(1, end) infinite;
          pointer-events: none;
        }

        /* --------- FASE A: brillo recorriendo el FONDO (izq -> der) --------- */
        .shine-fill {
          background: linear-gradient(
            90deg,
            transparent 0%,
            transparent 20%,
            #8B6914 35%,
            #D4A028 45%,
            #F4C430 50%,
            #D4A028 55%,
            #8B6914 65%,
            transparent 80%,
            transparent 100%
          );
          background-size: 300% 100%;
          animation: fill-sweep 4s ease-in-out infinite;
        }

        @keyframes fill-sweep {
          0% {
            background-position: -40% 50%;
            opacity: 0;
          }
          8% {
            background-position: -10% 50%;
            opacity: 1;
          }
          55% {
            background-position: 110% 50%;
            opacity: 1;
          }
          70% {
            background-position: 140% 50%;
            opacity: 0;
          }
          100% {
            background-position: 140% 50%;
            opacity: 0;
          }
        }

        /* --------- FASE B: "regreso" por el BORDE (der -> izq) --------- */
        .shine-border {
          border: 3px solid transparent;
          background: linear-gradient(
            -90deg,
            transparent 0%,
            transparent 20%,
            #8B6914 35%,
            #D4A028 45%,
            #F4C430 50%,
            #D4A028 55%,
            #8B6914 65%,
            transparent 80%,
            transparent 100%
          );
          background-clip: border-box;
          -webkit-mask: 
            linear-gradient(#fff 0 0) padding-box, 
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          background-size: 300% 100%;
          animation: border-run 4s ease-in-out infinite;
        }

        @keyframes border-run {
          0% {
            opacity: 0;
            background-position: 140% 50%;
          }
          55% {
            opacity: 0;
            background-position: 140% 50%;
          }
          60% {
            opacity: 1;
            background-position: 120% 50%;
          }
          92% {
            opacity: 1;
            background-position: -10% 50%;
          }
          100% {
            opacity: 0;
            background-position: -40% 50%;
          }
        }

        /* Respeta reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .shine-fill,
          .shine-border {
            animation: none !important;
            opacity: 0.35 !important;
          }
        }

        .edge-fade-mask{
          -webkit-mask-image:
              radial-gradient(120% 120% at 50% 50%, #000 74%, rgba(0,0,0,0) 100%),
              linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%),
              linear-gradient(to bottom, transparent 0%, #000 8%, #000 92%, transparent 100%);
          mask-image:
              radial-gradient(120% 120% at 50% 50%, #000 74%, rgba(0,0,0,0) 100%),
              linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%),
              linear-gradient(to bottom, transparent 0%, #000 8%, #000 92%, transparent 100%);
        }

        .title-track { letter-spacing: 0.02em; white-space: pre; }
        .title-glow { line-height: 1; opacity: 0.35; }
      `}</style>
    </section>
  );
};

export default HeroSection;
